import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  BookOpen,
  FileText,
  Scale,
  BarChart3,
  Plus,
  TrendingUp,
  TrendingDown,
  DollarSign,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useChartOfAccounts } from '@/hooks/queries/useAccounting';
import { useJournalEntries } from '@/hooks/queries/useJournalEntries';

export const Route = createFileRoute('/apps/accounting/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: AccountingDashboard,
});

function AccountingDashboard() {
  const { company } = useAuthStore();
  const { data: accounts = [] } = useChartOfAccounts(company?.id || '');
  const { data: entries = [] } = useJournalEntries(company?.id || '', {
    status: 'posted',
  });

  // Calculate summary stats
  const stats = {
    totalAccounts: accounts.length,
    activeAccounts: accounts.filter((a) => a.is_active).length,
    totalEntries: entries.length,
    draftEntries: entries.filter((e) => e.status === 'draft').length,
    totalDebit: entries.reduce((sum, e) => sum + e.total_debit, 0),
    totalCredit: entries.reduce((sum, e) => sum + e.total_credit, 0),
    assets: accounts
      .filter((a) => a.account_type === 'asset')
      .reduce((sum, a) => sum + a.current_balance, 0),
    liabilities: accounts
      .filter((a) => a.account_type === 'liability')
      .reduce((sum, a) => sum + a.current_balance, 0),
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">المحاسبة / Comptabilité</h1>
            <p className="text-muted-foreground mt-2">
              Système comptable algérien - Plan Comptable National (PCN)
            </p>
          </div>
          <Link to="/apps/accounting/entries/new">
            <Button className="bg-gradient-to-r from-emerald-500 to-teal-600">
              <Plus className="w-4 h-4 mr-2" />
              قيد جديد / Nouvelle écriture
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-500 to-cyan-600">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="text-white">
                  <p className="text-sm opacity-90">Comptes / الحسابات</p>
                  <p className="text-4xl font-bold mt-1">{stats.totalAccounts}</p>
                  <p className="text-xs mt-2 opacity-75">
                    {stats.activeAccounts} actifs
                  </p>
                </div>
                <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm">
                  <BookOpen className="w-10 h-10 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-green-500 to-emerald-600">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="text-white">
                  <p className="text-sm opacity-90">Écritures / القيود</p>
                  <p className="text-4xl font-bold mt-1">{stats.totalEntries}</p>
                  <p className="text-xs mt-2 opacity-75">
                    {stats.draftEntries} en brouillon
                  </p>
                </div>
                <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm">
                  <FileText className="w-10 h-10 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-500 to-indigo-600">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="text-white">
                  <p className="text-sm opacity-90">Actifs / الأصول</p>
                  <p className="text-2xl font-bold mt-1">
                    {formatCurrency(stats.assets)}
                  </p>
                  <div className="flex items-center gap-1 mt-2 text-xs opacity-75">
                    <TrendingUp className="w-3 h-3" />
                    <span>Classe 2, 3, 4, 5</span>
                  </div>
                </div>
                <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm">
                  <TrendingUp className="w-10 h-10 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-orange-500 to-red-600">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="text-white">
                  <p className="text-sm opacity-90">Passifs / الخصوم</p>
                  <p className="text-2xl font-bold mt-1">
                    {formatCurrency(stats.liabilities)}
                  </p>
                  <div className="flex items-center gap-1 mt-2 text-xs opacity-75">
                    <TrendingDown className="w-3 h-3" />
                    <span>Classe 1, 4 (passif)</span>
                  </div>
                </div>
                <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm">
                  <TrendingDown className="w-10 h-10 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Access */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/apps/accounting/chart">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-blue-500">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-xl">
                    <BookOpen className="w-8 h-8 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">Plan comptable</p>
                    <p className="text-sm text-muted-foreground">دليل الحسابات</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/apps/accounting/entries">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-green-500">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-xl">
                    <FileText className="w-8 h-8 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">Écritures comptables</p>
                    <p className="text-sm text-muted-foreground">قيود اليومية</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/apps/accounting/reports/trial-balance">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-purple-500">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-100 rounded-xl">
                    <Scale className="w-8 h-8 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium">Balance de vérification</p>
                    <p className="text-sm text-muted-foreground">ميزان المراجعة</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/apps/accounting/reports/general-ledger">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-cyan-500">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-cyan-100 rounded-xl">
                    <BarChart3 className="w-8 h-8 text-cyan-600" />
                  </div>
                  <div>
                    <p className="font-medium">Grand livre</p>
                    <p className="text-sm text-muted-foreground">دفتر الأستاذ</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Recent Entries */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Écritures récentes / القيود الأخيرة</CardTitle>
              <Link to="/apps/accounting/entries">
                <Button variant="outline" size="sm">
                  Voir tout
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {entries.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4 opacity-50" />
                <p className="text-lg font-medium">Aucune écriture comptable</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Commencez par créer votre première écriture
                </p>
                <Link to="/apps/accounting/entries/new">
                  <Button className="mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Créer une écriture
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-2">
                {entries.slice(0, 5).map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <p className="font-mono font-bold">{entry.entry_number}</p>
                        <p className="text-sm text-muted-foreground">
                          {entry.description || 'Sans description'}
                        </p>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                        <span>
                          {new Date(entry.entry_date).toLocaleDateString('fr-FR')}
                        </span>
                        <span>•</span>
                        <span>Débit: {formatCurrency(entry.total_debit)}</span>
                        <span>•</span>
                        <span>Crédit: {formatCurrency(entry.total_credit)}</span>
                      </div>
                    </div>
                    <Link to={`/apps/accounting/entries/${entry.id}`}>
                      <Button variant="ghost" size="sm">
                        Voir
                      </Button>
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Information Card */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Scale className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-blue-900">
                  Système comptable algérien / النظام المحاسبي الجزائري
                </h3>
                <p className="text-sm text-blue-700 mt-2">
                  Ce module est conforme au Plan Comptable National (PCN) algérien et aux
                  normes SCF (Système Comptable Financier). Il permet la gestion complète de
                  la comptabilité en partie double avec support bilingue français/arabe.
                </p>
                <div className="grid grid-cols-2 gap-2 mt-4 text-xs text-blue-700">
                  <div>✓ Plan comptable PCN (Classes 1-9)</div>
                  <div>✓ Écritures en partie double</div>
                  <div>✓ Balance de vérification</div>
                  <div>✓ Grand livre</div>
                  <div>✓ Conformité fiscale algérienne</div>
                  <div>✓ Support bilingue AR/FR</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
